import { uniqueId } from 'lodash-es'
import { action } from 'mobx'
import type { Observable } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import type { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { move } from '@/utils/array-util'
import { tapAction, toStream } from '@/utils/mobx-util'

/**
 * Keeps the lifecycle managing exactly the view models in the list, so items
 * added later follow it and removed ones are deactivated. Pass the returned
 * stream to the same lifecycle.
 *
 * An item that moves between lists must stay in one list's lifecycle: a move
 * runs both lists' syncs in an unspecified order, so the item could end up
 * deactivated. Manage moving items from a list that contains both ends of the
 * move, like the questions of the whole tree.
 *
 * Not a `ViewModelList`, which creates its view models from models and drops
 * them when deactivated: these view models are the legend's state, so they
 * outlive deactivation.
 *
 * @param lifecycle
 * @param getItems
 */
export function manageEach(
  lifecycle: LifecycleTracker,
  getItems: () => readonly (ActivationLifecycle | null)[],
): Observable<unknown> {
  // Kept across activations, so items removed while inactive are let go of.
  let managed = new Set<ActivationLifecycle>()
  return toStream(() => getItems().slice(), true).pipe(
    tapAction((items) => {
      const next = new Set(
        items.filter((item): item is ActivationLifecycle => !!item),
      )
      for (const item of managed) {
        if (!next.has(item)) {
          lifecycle.remove(item)
        }
      }
      for (const item of next) {
        lifecycle.manage(item)
      }
      managed = next
    }),
  )
}

/**
 * Moves an item within the array, clamping the target index, which
 * {@link move} doesn't: moving the first row up or the last one down with the
 * keyboard asks for an index past either end.
 *
 * @param items
 * @param fromIndex
 * @param toIndex
 */
export const moveItem = action(function moveItem<T>(
  items: T[],
  fromIndex: number,
  toIndex: number,
) {
  const target = Math.max(0, Math.min(toIndex, items.length - 1))
  if (fromIndex !== target && fromIndex >= 0) {
    move(items, fromIndex, target)
  }
})

/**
 * Creates a client-only ID. The prefix becomes the stem of IDs generated from
 * labels.
 *
 * @param prefix
 */
export function newCid(prefix: string) {
  return uniqueId(`${prefix}-`)
}
